// import libraries
import { createApp } from 'vue'
import Vuex from "vuex"
import VuexPersistence from 'vuex-persist'
import { createRouter, createWebHistory } from 'vue-router'
import axios from 'axios'

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, mdi } from "vuetify/lib/iconsets/mdi";
import "@mdi/font/css/materialdesignicons.css"
import '../scss/main.scss'; // custom css

//import views
import BlankLayout from './layouts/BlankLayout.vue'
import AppLayout from './layouts/AppLayout.vue'

// import page components
import App from './App.vue'
import Dashboard from './components/Dashboard.vue'
import Properties from './components/Properties.vue'
import Vehicles from './components/Vehicles.vue'
import Collectibles from './components/Collectibles.vue'
import OtherAssets from './components/OtherAssets.vue'
import Salaries from './components/Salaries.vue'
import Expenses from './components/Expenses.vue'
import Cash from './components/Cash.vue'
import Loans from './components/Loans.vue'
import BrokerTxns from './components/BrokerAccounts.vue'
import SecuritiesSummary from './components/SecuritiesSummary.vue'
import SecurityPosition from './components/SecurityPosition.vue'
import Login from './components/Login.vue'
import Homepage from './components/Homepage.vue'
import BankAccountSummary from './components/BankAccountSummary.vue'
import BankAccountTxns from './components/BankAccountTxns.vue'
import ExpenseTxns from './components/ExpenseTxns.vue'
import SuperFunds from './components/SuperFunds.vue'
import SecurityCategories from './components/SecurityCategories.vue'
import SecurityCategoriesConfig from './components/SecurityCategoriesConfig.vue'
import PortfolioSegmentation from './components/PortfolioSegmentation.vue'
import Notifications from './components/Notifications.vue'
import Watchlist from './components/Watchlist.vue'
import Modelling from './components/Modelling.vue'
import InvestorQuotes from './components/InvestorQuotes.vue'
import Reports from './components/Reports.vue'
import CgtDisposals from './components/CgtDisposals.vue'
import Acquisitions from './components/Acquisitions.vue'
import InvestmentPerformance from './components/InvestmentPerformance.vue'
import Admin from './components/Admin.vue'
import PriceBackfill from './components/PriceBackfill.vue'
import DividendBackfill from './components/DividendBackfill.vue'

// Vuex Store stuff

const store = new Vuex.Store({
    state: {
        user: null,
        token: null,
        refreshToken: null,
        snackbar: { show: false, message: '', color: 'error' },
        notificationChange: 0
    },
    mutations: {
        setUser(state, user) {
            state.user = user;
        },
        setToken(state, token) {
            state.token = token;
        },
        setRefreshToken(state, token) {
            state.refreshToken = token;
        },
        destroyToken(state){
            state.token = null;
            state.refreshToken = null;
        },
        showSnackbar(state, { message, color = 'error' }) {
            state.snackbar = { show: true, message, color };
        },
        hideSnackbar(state) {
            state.snackbar.show = false;
        },
        bumpNotificationChange(state) {
            state.notificationChange++;
        }
    },
    actions: {},
    getters: {
        isLoggedIn(state) {
            return !!state.token;
        },
    },
    plugins: [new VuexPersistence().plugin] // Using local browser storage which is not best practice (insecure)
});

// Vuetify stuff

const vuetify = createVuetify({
    icons: {
        defaultSet: "mdi",
        aliases,
        sets: {
          mdi,
        },
      },
    components,
    directives,
    theme: {
        defaultTheme: 'light',
        //
    },
  })

// JWT expiry timer

function getTokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null;
    } catch { return null; }
}

let expiryTimer = null;

// Single shared refresh state — prevents duplicate /auth/refresh calls from the
// startup timer and the Axios 401 interceptor racing with the same refresh token.
let refreshPromise = null;
let failedQueue = [];

function processQueue(error, token = null) {
    failedQueue.forEach(({ resolve, reject }) => {
        error ? reject(error) : resolve(token);
    });
    failedQueue = [];
}

function doRefresh() {
    if (refreshPromise) return refreshPromise;

    const refreshToken = store.state.refreshToken;
    if (!refreshToken) {
        store.commit('destroyToken');
        localStorage.clear();
        router.push('/');
        return Promise.reject(new Error('No refresh token'));
    }

    refreshPromise = axios.post('/auth/refresh', { refresh_token: refreshToken })
        .then(({ data }) => {
            store.commit('setToken', data.access_token);
            store.commit('setRefreshToken', data.refresh_token);
            processQueue(null, data.access_token);
            return data.access_token;
        })
        .catch((err) => {
            processQueue(err, null);
            store.commit('destroyToken');
            localStorage.clear();
            router.push('/');
            throw err;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
}

async function silentRefresh() {
    try {
        await doRefresh();
    } catch {
        // logout already handled in doRefresh
    }
}

function startExpiryTimer(token) {
    clearExpiryTimer();
    const expiry = getTokenExpiry(token);
    if (!expiry) return;
    const ms = expiry - Date.now();
    if (ms <= 0) {
        silentRefresh();
        return;
    }
    expiryTimer = setTimeout(() => {
        silentRefresh();
    }, ms);
}

function clearExpiryTimer() {
    if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
}

// Check token expiry on wake from sleep
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        const token = store.state.token;
        if (token && getTokenExpiry(token) <= Date.now()) {
            clearExpiryTimer();
            silentRefresh();
        }
    }
});

// Axios global handling

axios.interceptors.request.use((config) => {
    const token = store.state.token;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if ((error.response?.status === 401 || error.response?.status === 403)
            && !originalRequest._retry) {

            // Don't retry refresh requests themselves
            if (originalRequest.url === '/auth/refresh') {
                store.commit('destroyToken');
                localStorage.clear();
                router.push('/');
                return new Promise(() => {});
            }

            // If a refresh is already in flight (from this interceptor or silentRefresh),
            // queue this request to retry once it settles.
            if (refreshPromise) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                });
            }

            originalRequest._retry = true;

            try {
                const newToken = await doRefresh();
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);
            } catch {
                return new Promise(() => {});
            }
        }

        return Promise.reject(error);
    }
  );
  
  axios.defaults.baseURL = process.env.VUE_APP_BACKEND_BASE_URL; 

// Set Vue Router //

  const routes = [
    { 
        path: '/dashboard',
        meta: { layout: AppLayout }, 
        component: Dashboard 
    },
    { 
        path: '/properties',
        meta: { layout: AppLayout }, 
        component: Properties 
    },
    {
        path: '/vehicles',
        meta: { layout: AppLayout },
        component: Vehicles
    },
    {
        path: '/collectibles',
        meta: { layout: AppLayout },
        component: Collectibles
    },
    {
        path: '/otherassets',
        meta: { layout: AppLayout },
        component: OtherAssets
    },
    { 
        path: '/salaries',
        meta: { layout: AppLayout }, 
        component: Salaries 
    },
    { 
      path: '/expenses',
      meta: { layout: AppLayout }, 
      component: Expenses
    },
    { 
      path: '/expensetxns/:id?',
      meta: { layout: AppLayout }, 
      component: ExpenseTxns
    },
    { 
        path: '/cash',
        meta: { layout: AppLayout }, 
        component: Cash 
    },
    { 
        path: '/loans', 
        meta: { layout: AppLayout },
        component: Loans 
    },
    { 
      path: '/superfunds', 
      meta: { layout: AppLayout },
      component: SuperFunds 
    },
    { 
        path: '/brokeraccounts/:id', 
        meta: { layout: AppLayout },
        component: BrokerTxns,
        props: true 
    },
    { 
      path: '/brokeraccounts/:brokerid/position/:ticker', 
      meta: { layout: AppLayout },
      component: SecurityPosition,
      props: true 
  },
  { 
    path: '/securitiessummary', 
    meta: { layout: AppLayout },
    component: SecuritiesSummary 
  },
  { 
    path: '/securitycategories', 
    meta: { layout: AppLayout },
    component: SecurityCategories 
  },
  { 
    path: '/securitycategories/config', 
    meta: { layout: AppLayout },
    component: SecurityCategoriesConfig
  },
  { 
    path: '/portfolioallocation', 
    meta: { layout: AppLayout },
    component: PortfolioSegmentation
  },
  { 
    path: '/bankaccountsummary', 
    meta: { layout: AppLayout },
    component: BankAccountSummary 
  },
  { 
    path: '/bankaccounttxns/:id', 
    meta: { layout: AppLayout },
    component: BankAccountTxns 
  },
  {
    path: '/notifications',
    meta: { layout: AppLayout },
    component: Notifications
  },
  {
    path: '/modelling',
    meta: { layout: AppLayout },
    component: Modelling
  },
  {
    path: '/watchlist',
    meta: { layout: AppLayout },
    component: Watchlist
  },
  {
    path: '/quotes',
    meta: { layout: AppLayout },
    component: InvestorQuotes
  },
  {
    path: '/reports',
    meta: { layout: AppLayout },
    component: Reports
  },
  {
    path: '/reports/cgt-disposals',
    meta: { layout: AppLayout },
    component: CgtDisposals
  },
  {
    path: '/reports/acquisitions',
    meta: { layout: AppLayout },
    component: Acquisitions
  },
  {
    path: '/reports/investment-performance',
    meta: { layout: AppLayout },
    component: InvestmentPerformance
  },
  {
    path: '/admin',
    meta: { layout: AppLayout },
    component: Admin
  },
  {
    path: '/admin/price-backfill',
    meta: { layout: AppLayout },
    component: PriceBackfill
  },
  {
    path: '/admin/dividend-backfill',
    meta: { layout: AppLayout },
    component: DividendBackfill
  },
  {
      path: '/login',
      meta: { layout: BlankLayout },
      component: Login
  },
  { 
    path: '/',
    meta: { layout: BlankLayout }, 
    component: Homepage 
}
  ];

const router = createRouter({
    history: createWebHistory(),
    routes
});

store.subscribe((mutation) => {
    if (mutation.type === 'setToken' && mutation.payload) {
        startExpiryTimer(mutation.payload);
    } else if (mutation.type === 'destroyToken') {
        clearExpiryTimer();
    }
});

if (store.state.token) {
    startExpiryTimer(store.state.token);
}

router.beforeEach((to, from, next) => {
    const requiresAuth = to.meta.layout === AppLayout;
    const token = store.state.token;
    const refreshToken = store.state.refreshToken;
    const isLoggedIn = token && getTokenExpiry(token) > Date.now();
    const canRefresh = !!refreshToken;

    if (requiresAuth && !isLoggedIn && !canRefresh) {
        if (token) {
            store.commit('destroyToken');
            localStorage.clear();
        }
        next('/');
    } else if (to.path === '/login' && (isLoggedIn || canRefresh)) {
        next('/dashboard');
    } else {
        next();
    }
});

/////////////////////


const app = createApp(App);

app.use(router);
app.use(store);
app.use(vuetify);

app.mount('#app');


